import { clientTelemetrySettings, telemetryExporters } from "@lafayette/domain";
import * as Sentry from "@sentry/react";

import type { AdminPublicConfig } from "./config/public-config";

let initialized = false;

/**
 * The click and input breadcrumbs of the error reporter name the element that was used: its tag, id, classes and
 * the attributes `aria-label`, `type`, `name`, `title` and `alt`. Under `/customers` the screen shows what Lafayette
 * remembers about a person, so those pages drop the message and the data of such a breadcrumb (CHT-721). The pages
 * also keep every fact value out of those attributes; this is the second guard, and it holds for a later change.
 * Navigation, request and console breadcrumbs stay as they are: they carry ids and codes, never a value.
 */
export function scrubBreadcrumb(
  breadcrumb: Sentry.Breadcrumb,
  pathname: string,
): Sentry.Breadcrumb {
  const underCustomers = pathname === "/customers" || pathname.startsWith("/customers/");
  if (!underCustomers) return breadcrumb;
  if (breadcrumb.category !== "ui.click" && breadcrumb.category !== "ui.input") return breadcrumb;
  const { message: _message, data: _data, ...kept } = breadcrumb;
  return kept;
}

/**
 * The Sentry options of the admin (CHT-770). Release, build id (`dist`) and environment are the metadata that a source map upload and a search
 * use: `release` is `lafayette-admin@<version>+<sha>` and `dist` is the build id. Every event, transaction and breadcrumb passes the shared
 * redaction of `@lafayette/domain` before it leaves the browser; `sendDefaultPii` is always `false`. A `/customers` page also drops the label of
 * what was clicked (CHT-721) before the shared redaction runs.
 */
export function adminSentryOptions(
  config: AdminPublicConfig,
  pathname: () => string = () => window.location.pathname,
): Sentry.BrowserOptions {
  const settings = clientTelemetrySettings(config.telemetryDebug, config.environment);
  const exporters = telemetryExporters(settings);
  return {
    dsn: config.sentryDsn ?? undefined,
    environment: config.environment,
    release: `lafayette-admin@${config.version}+${config.gitSha}`,
    dist: config.buildId,
    sendDefaultPii: settings.sendDefaultPii,
    beforeSend: (event) => exporters.event(event),
    beforeSendTransaction: (event) => exporters.event(event),
    beforeBreadcrumb: (breadcrumb) => exporters.breadcrumb(scrubBreadcrumb(breadcrumb, pathname())),
    tracesSampleRate: 0,
    initialScope: {
      tags: {
        app_surface: "admin",
        build_id: config.buildId,
        git_sha: config.gitSha,
      },
    },
  };
}

export function initializeAdminObservability(config: AdminPublicConfig): boolean {
  if (!config.sentryDsn) return false;
  if (initialized) return true;

  Sentry.init(adminSentryOptions(config));
  initialized = true;

  if (config.sentrySmokeEnabled) {
    Sentry.captureException(new Error("OBSERVABILITY_SMOKE_ADMIN"), {
      tags: { smoke_test: "phase-1" },
    });
  }

  return true;
}
